import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, api } from "../lib/api";
import { ArrowDown, ArrowUp, CheckCircle2, ChevronsDown, ChevronsUp, Edit3, Play, Plus, RefreshCw, Save, Sparkles, Tags, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ColorPicker from "../components/ColorPicker";
import IconPicker from "../components/IconPicker";
import { CurrencyIcon, currencyLabel, sortCurrencies } from "../lib/currencyDisplay";
import { DEFAULT_OBS_CONFIG, normalizeObsConfig, useObs } from "../hooks/useObs";

const OBS_LOCAL_CONFIG_KEY = "thebarracksbunny.obs.localConfig";
const defaultObsConfig = DEFAULT_OBS_CONFIG;

function loadLocalObsConfig() {
  try {
    const parsed = JSON.parse(localStorage.getItem(OBS_LOCAL_CONFIG_KEY) || "{}");
    return normalizeObsConfig({ ...defaultObsConfig, ...parsed });
  } catch (err) {
    return defaultObsConfig;
  }
}

const emptyForm = {
  name: "",
  description: "",
  icon: "Sparkles",
  accent_color: "#0A84FF",
  enabled: true,
  public: true,
  cost_type: "free_currency",
  cost_amount: 0,
  category_tags: ["general"],
  queue_type: "media",
  cooldown_seconds: 0,
  cooldown_scope: "none",
  approval_required: false,
  platform_redeem_aliases: [],
  twitch_reward_aliases: [],
  kick_reward_aliases: [],
  kick_reward_id: "",
  actions: [],
};

const emptyCategoryForm = {
  name: "",
  slug: "",
  color: "#8E8E93",
  enabled: true,
};

const queueOptions = [
  ["camera", "Camera"],
  ["media", "Media"],
  ["rpg", "RPG"],
  ["chat", "Chat"],
];

const cooldownScopeOptions = [
  ["none", "None"],
  ["per_user", "Per user"],
  ["global", "Global"],
];

const actionTypeOptions = [
  ["show_video_media", "Show video media"],
  ["clear_video_media", "Clear video media"],
  ["obs_scene", "OBS scene"],
  ["obs_filter", "OBS filter"],
  ["obs_filter_enable", "Enable OBS filter"],
  ["obs_filter_disable", "Disable OBS filter"],
  ["show_source", "Show OBS source"],
  ["hide_source", "Hide OBS source"],
  ["toggle_source", "Toggle OBS source"],
  ["delay", "Delay"],
  ["log_message", "Log message"],
  ["ai_generate_text", "AI text generator"],
  ["tts_generate_audio", "TTS / generated speech"],
  ["twitch_create_clip", "Create Twitch clip"],
  ["vip_assign", "Steal / assign VIP"],
  ["play_sound", "Play sound"],
  ["chat_message", "Chat message"],
  ["rpg_event", "RPG event"],
  ["give_item", "Give item"],
  ["trigger_interaction", "Trigger interaction"],
  ["random_action", "Random action"],
];

export default function InteractionsAdmin() {
  const [interactions, setInteractions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [testSubmittingId, setTestSubmittingId] = useState(null);
  const [platformRewardOptions, setPlatformRewardOptions] = useState({ twitch: [], kick: [] });
  const [kickSyncingId, setKickSyncingId] = useState(null);
  const [adminLoadError, setAdminLoadError] = useState("");

  const load = useCallback(async () => {
    setAdminLoadError("");
    try {
      const [i, c, tags] = await Promise.all([
        api.get("/admin/interactions"),
        api.get("/currencies"),
        api.get("/admin/interaction-categories"),
      ]);
      setInteractions(i.data);
      setCurrencies(c.data);
      setCategories(tags.data);
      try {
        const { data: platformEvents } = await api.get("/admin/platform-events", { params: { limit: 200, source: "all" } });
        const eventOptions = rewardAliasOptionsFromPlatformEvents(platformEvents.items || []);
        try {
          const { data: kickRewards } = await api.get("/admin/kick/rewards");
          const liveKick = (kickRewards.items || []).flatMap((reward) => [reward.id, reward.title]).filter(Boolean);
          setPlatformRewardOptions(mergeRewardAliasOptions(eventOptions, { twitch: [], kick: liveKick }));
        } catch (kickErr) {
          setPlatformRewardOptions(eventOptions);
        }
      } catch (eventsErr) {
        setPlatformRewardOptions({ twitch: [], kick: [] });
      }
    } catch (err) {
      setInteractions([]);
      setCurrencies([]);
      setCategories([]);
      setPlatformRewardOptions({ twitch: [], kick: [] });
      setAdminLoadError(err.response?.data?.detail || "Admin interaction tooling could not reach the configured backend.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const costOptions = useMemo(() => sortCurrencies(currencies), [currencies]);
  const currencyMap = useMemo(() => {
    const map = {};
    currencies.forEach((c) => { map[c.id] = c; });
    return map;
  }, [currencies]);
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((category) => { map[category.slug] = category; });
    return map;
  }, [categories]);
  const rewardAliasOptions = useMemo(() => mergeRewardAliasOptions(
    rewardAliasOptionsFromInteractions(interactions),
    platformRewardOptions,
  ), [interactions, platformRewardOptions]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = prepareInteractionPayload(form);
      if (editingId) {
        await api.patch(`/admin/interactions/${editingId}`, payload);
        toast.success("Interaction updated");
      } else {
        await api.post("/admin/interactions", payload);
        toast.success("Interaction created");
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Save failed");
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      description: item.description || "",
      icon: item.icon || "Sparkles",
      accent_color: item.accent_color || "#0A84FF",
      enabled: item.enabled !== false,
      public: item.public !== false,
      cost_type: item.cost_type || "free_currency",
      cost_amount: item.cost_amount || 0,
      category_tags: item.category_tags || [],
      queue_type: item.queue_type || "media",
      cooldown_seconds: item.cooldown_seconds || 0,
      cooldown_scope: item.cooldown_scope || "none",
      approval_required: item.approval_required === true,
      platform_redeem_aliases: item.platform_redeem_aliases || [],
      twitch_reward_aliases: item.twitch_reward_aliases || item.platform_redeem_aliases || [],
      kick_reward_aliases: item.kick_reward_aliases || item.platform_redeem_aliases || [],
      kick_reward_id: item.kick_reward_id || "",
      actions: (item.actions || []).map(actionFromApi),
    });
  };

  const syncKickReward = async () => {
    if (!editingId) return;
    setKickSyncingId(editingId);
    try {
      const { data } = await api.post(`/admin/interactions/${editingId}/kick-reward/sync`);
      setForm((current) => ({
        ...current,
        kick_reward_id: data.reward_id || "",
        kick_reward_aliases: data.interaction?.kick_reward_aliases || current.kick_reward_aliases,
      }));
      toast.success(`Kick reward ${data.action}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kick reward sync failed");
    } finally {
      setKickSyncingId(null);
    }
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategoryId) {
        await api.patch(`/admin/interaction-categories/${editingCategoryId}`, categoryForm);
        toast.success("Category updated");
      } else {
        await api.post("/admin/interaction-categories", categoryForm);
        toast.success("Category created");
      }
      setCategoryForm(emptyCategoryForm);
      setEditingCategoryId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Category save failed");
    }
  };

  const editCategory = (category) => {
    setEditingCategoryId(category.slug);
    setCategoryForm({
      name: category.name || "",
      slug: category.slug || "",
      color: category.color || "#8E8E93",
      enabled: category.enabled !== false,
    });
  };

  const cancelCategory = () => {
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm);
  };

  const toggleCategory = async (category) => {
    await api.patch(`/admin/interaction-categories/${category.slug}`, {
      name: category.name,
      slug: category.slug,
      color: category.color,
      enabled: category.enabled === false,
    });
    load();
  };

  const removeCategory = async (category) => {
    if (!window.confirm(`Delete ${category.name}? This removes the tag from interactions that use it.`)) return;
    await api.delete(`/admin/interaction-categories/${category.slug}`);
    toast.success("Category deleted");
    if (editingCategoryId === category.slug) cancelCategory();
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    await api.delete(`/admin/interactions/${item.id}`);
    toast.success("Interaction deleted");
    if (editingId === item.id) {
      setEditingId(null);
      setForm(emptyForm);
    }
    load();
  };

  const createTestRequestForInteraction = async (interaction) => {
    const requesterId = window.prompt("Test requester ID", "local-admin-test");
    if (requesterId === null) return;
    const cleanRequesterId = requesterId.trim();
    if (!cleanRequesterId) {
      toast.error("Test requester ID is required");
      return;
    }
    const viewerInputLabel = viewerInputLabelForInteraction(interaction);
    const viewerInput = viewerInputLabel
      ? window.prompt(`Optional ${viewerInputLabel}`, "")
      : "";
    if (viewerInput === null) return;
    setTestSubmittingId(interaction.id);
    try {
      const inputValues = {
        requester_display_name: "Admin Test",
        requester_username: "admin-test",
      };
      if (viewerInputLabel && viewerInput.trim()) {
        inputValues.viewer_input = viewerInput.trim();
      }
      const { data } = await api.post("/interaction-requests", {
        interaction_id: interaction.id,
        requester_id: cleanRequesterId,
        source: "admin_test",
        input_values: inputValues,
      });
      toast.success(`Test request created: ${data.status || "queued"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create test request");
    } finally {
      setTestSubmittingId(null);
    }
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  return (
    <div className="space-y-6" data-testid="interactions-admin-page">
      <div>
        <div className="label-eyebrow">Admin configuration</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[hsl(var(--primary))]" /> Interaction Builder
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Create and configure interactions.</p>
      </div>

      {adminLoadError && (
        <div className="rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-red-100" data-testid="admin-interactions-backend-error">
          {adminLoadError}
        </div>
      )}

      <section className="surface p-5 space-y-4" data-testid="interaction-categories-admin">
        <SectionHeader
          eyebrow="Viewer identity"
          title="Category Manager"
          description="Categories are viewer-facing tags used for themes, filtering, and future game/event availability."
          icon={Tags}
        />

        <form onSubmit={submitCategory} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Input label="Name" value={categoryForm.name} onChange={(v) => setCategoryForm({ ...categoryForm, name: v })} required test="category-name" />
          <Input label="Slug" value={categoryForm.slug} onChange={(v) => setCategoryForm({ ...categoryForm, slug: v })} test="category-slug" />
          <ColorPicker label="Color" value={categoryForm.color} onChange={(v) => setCategoryForm({ ...categoryForm, color: v })} fallback="#8E8E93" test="category-color" />
          <Toggle label="Enabled" checked={categoryForm.enabled} onChange={(v) => setCategoryForm({ ...categoryForm, enabled: v })} test="category-enabled" />
          <div className="flex gap-2">
            <button type="submit" className="bg-[hsl(var(--primary))] text-white text-sm px-3 py-2 rounded flex items-center gap-2" data-testid="save-category">
              {editingCategoryId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editingCategoryId ? "Save" : "Add"}
            </button>
            {editingCategoryId && (
              <button type="button" onClick={cancelCategory} className="border border-[hsl(var(--border))] text-sm px-3 py-2 rounded" data-testid="cancel-category">Cancel</button>
            )}
          </div>
        </form>

        <div className="flex flex-wrap gap-2">
          {categories.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No categories yet.</div>}
          {categories.map((category) => (
            <div key={category.slug} className="border border-[hsl(var(--border))] rounded px-3 py-2 flex items-center gap-2 bg-[hsl(var(--surface-2))]" data-testid={`category-${category.slug}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: category.color }} />
              <span className={category.enabled === false ? "text-[hsl(var(--muted-foreground))] line-through" : ""}>{category.name}</span>
              <code className="text-[10px] text-[hsl(var(--muted-foreground))]">{category.slug}</code>
              <button onClick={() => toggleCategory(category)} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-white">{category.enabled === false ? "Enable" : "Disable"}</button>
              <button onClick={() => editCategory(category)} className="p-1 text-[hsl(var(--primary))]" data-testid={`edit-category-${category.slug}`}><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => removeCategory(category)} className="p-1 text-[hsl(var(--destructive))]" data-testid={`delete-category-${category.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </section>

      <form onSubmit={submit} className="surface p-5 space-y-4" data-testid="interaction-form">
        <SectionHeader
          eyebrow={editingId ? "Editing interaction" : "New interaction"}
          title={editingId ? "Edit Interaction" : "Create Interaction"}
          description="Interactions are redeemable or triggerable actions. Category tags describe the interaction; queue type controls future execution routing."
          icon={Sparkles}
        />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required test="interaction-name" />
          <Number label="Cost" value={form.cost_amount} onChange={(v) => setForm({ ...form, cost_amount: v })} test="interaction-cost" />
          <Select label="Cost type" value={form.cost_type} onChange={(v) => setForm({ ...form, cost_type: v })} options={costOptions.map((c) => [c.id, currencyLabel(c)])} test="interaction-cost-type" />
          <ColorPicker label="Accent color" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} fallback="#0A84FF" test="interaction-accent-color" />
          <div className="flex items-end gap-4">
            <Toggle label="Enabled" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} test="interaction-enabled" />
            <Toggle label="Public" checked={form.public} onChange={(v) => setForm({ ...form, public: v })} test="interaction-public" />
          </div>
        </div>

        <div>
          <div className="label-eyebrow mb-1.5">Description</div>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm"
            data-testid="interaction-description"
          />
        </div>

        <IconPicker label="Icon" value={form.icon} color={form.accent_color} onChange={(icon) => setForm({ ...form, icon })} test="interaction-icon-picker" />
        <CategoryPicker categories={categories} value={form.category_tags} onChange={(category_tags) => setForm({ ...form, category_tags })} />
        <div className="max-w-sm">
          <Select label="Queue type" value={form.queue_type} onChange={(v) => setForm({ ...form, queue_type: v })} options={queueOptions} test="interaction-queue-type" />
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">Choose one execution lane. This is not shown to viewers.</p>
          <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] space-y-1">
            <div><span className="text-white">camera</span> = OBS/camera visual effects</div>
            <div><span className="text-white">media</span> = audio/video/TTS/alerts that should not overlap</div>
            <div><span className="text-white">rpg</span> = RPG/game logic systems</div>
            <div><span className="text-white">chat</span> = chat-only interactions</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg">
          <Number label="Cooldown seconds" value={form.cooldown_seconds} onChange={(v) => setForm({ ...form, cooldown_seconds: v })} test="interaction-cooldown-seconds" />
          <Select label="Cooldown scope" value={form.cooldown_scope} onChange={(v) => setForm({ ...form, cooldown_scope: v })} options={cooldownScopeOptions} test="interaction-cooldown-scope" />
          <p className="md:col-span-2 text-xs text-[hsl(var(--muted-foreground))]">Stored for future redeem and queue execution. This does not enforce cooldowns yet.</p>
        </div>

        <div className="max-w-sm">
          <Toggle label="Requires approval before queue" checked={form.approval_required} onChange={(v) => setForm({ ...form, approval_required: v })} test="interaction-approval-required" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RewardAliasPicker
            label="Twitch reward names"
            help="Channel Point reward titles or IDs that should trigger this interaction. Known options come from saved interactions for now."
            value={form.twitch_reward_aliases || []}
            options={rewardAliasOptions.twitch}
            placeholder="Hydrate"
            onChange={(twitch_reward_aliases) => setForm({ ...form, twitch_reward_aliases })}
            test="interaction-twitch-reward-aliases"
          />
          <RewardAliasPicker
            label="Kick reward names"
            help="Kick reward titles or IDs that should trigger this interaction. Known options come from saved interactions for now."
            value={form.kick_reward_aliases || []}
            options={rewardAliasOptions.kick}
            placeholder="Hydrate"
            onChange={(kick_reward_aliases) => setForm({ ...form, kick_reward_aliases })}
            test="interaction-kick-reward-aliases"
          />
          <div className="md:col-start-2 text-xs text-[hsl(var(--muted-foreground))]">
            {form.kick_reward_id ? <div className="mb-2">Linked reward ID: <span className="font-mono text-white">{form.kick_reward_id}</span></div> : null}
            {editingId ? (
              <button
                type="button"
                onClick={syncKickReward}
                disabled={kickSyncingId === editingId}
                className="border border-[#53fc18]/50 text-[#53fc18] px-3 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="sync-kick-reward"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${kickSyncingId === editingId ? "animate-spin" : ""}`} />
                {kickSyncingId === editingId ? "Syncing" : form.kick_reward_id ? "Update Kick reward" : "Create Kick reward"}
              </button>
            ) : <div>Save the interaction before creating its Kick reward.</div>}
          </div>
        </div>

        <ActionEditor actions={form.actions} onChange={(actions) => setForm({ ...form, actions })} />

        <div className="flex gap-2">
          <button type="submit" className="bg-[hsl(var(--primary))] text-white text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="save-interaction">
            {editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editingId ? "Save interaction" : "Create interaction"}
          </button>
          {editingId && (
            <button type="button" onClick={cancel} className="border border-[hsl(var(--border))] text-sm px-4 py-2 rounded flex items-center gap-2" data-testid="cancel-edit-interaction">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>
      </form>

      <section className="surface overflow-hidden">
        <div className="p-5 border-b border-[hsl(var(--border))]">
          <SectionHeader
            eyebrow="Saved definitions"
            title="Existing Interactions"
            description="Review saved interactions, their viewer-facing categories, and their future execution lane."
            icon={Sparkles}
          />
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="py-3 px-4">Name</th>
              <th>Cost</th>
              <th>Categories</th>
              <th>Queue</th>
              <th>Cooldown</th>
              <th>Actions</th>
              <th>Status</th>
              <th>Visibility</th>
              <th className="text-right pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {interactions.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-[hsl(var(--muted-foreground))]">No interactions yet.</td></tr>}
            {interactions.map((item) => {
              const currency = currencyMap[item.cost_type];
              const accentColor = item.accent_color || "#0A84FF";
              return (
                <tr key={item.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]" style={{ borderLeft: `3px solid ${accentColor}` }}>
                  <td className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      <CurrencyIcon currency={{ icon: item.icon, color: accentColor }} className="w-4 h-4 mt-0.5" />
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{item.description}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: currency?.color }}>{item.cost_amount} {currencyLabel(currency || { id: item.cost_type })}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(item.category_tags || []).map((slug) => {
                        const category = categoryMap[slug];
                        return <span key={slug} className="text-[10px] px-2 py-0.5 rounded border" style={{ borderColor: category?.color || "hsl(var(--border))", color: category?.color || "hsl(var(--muted-foreground))" }}>{category?.name || slug}</span>;
                      })}
                    </div>
                  </td>
                  <td><span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{item.queue_type || "media"}</span></td>
                  <td><span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{cooldownLabel(item)}</span></td>
                  <td><span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">{(item.actions || []).length}</span></td>
                  <td>{item.enabled ? "Enabled" : "Disabled"}</td>
                  <td>{item.public ? "Public" : "Private"}</td>
                  <td className="text-right pr-4">
                    <button
                      onClick={() => createTestRequestForInteraction(item)}
                      disabled={testSubmittingId === item.id}
                      className="p-1.5 text-emerald-300 disabled:opacity-40"
                      title="Create test request"
                      data-testid={`test-interaction-${item.id}`}
                    >
                      <Sparkles className="w-4 h-4" />
                    </button>
                    <button onClick={() => edit(item)} className="p-1.5 text-[hsl(var(--primary))]" data-testid={`edit-interaction-${item.id}`}><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(item)} className="p-1.5 text-[hsl(var(--destructive))]" data-testid={`delete-interaction-${item.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

    </div>
  );
}

function InteractionRequestQueueViewer({
  definitions,
  creatorForm,
  onCreatorFormChange,
  creatorSubmitting,
  creatorMessage,
  creatorError,
  onCreateRequest,
  requests,
  loading,
  error,
  onRefresh,
  onApprove,
  onReject,
}) {
  return (
    <section className="surface overflow-hidden space-y-0" data-testid="interaction-request-queue-viewer">
      <div className="p-5 border-b border-[hsl(var(--border))]">
        <SectionHeader
          eyebrow="Local/dev admin tooling"
          title="Test Interaction Request Creator"
          description="Local/dev test request creator. This is not public viewer redeem UI."
          icon={Plus}
        />
        <form onSubmit={onCreateRequest} className="mt-4 space-y-4" data-testid="interaction-test-request-form">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <ComboSelect
              label="Interaction definition"
              value={creatorForm.interaction_id}
              onChange={(interaction_id) => onCreatorFormChange({ ...creatorForm, interaction_id })}
              options={definitions}
              placeholder={definitions.length === 0 ? "No definitions loaded" : "Select interaction"}
              disabled={definitions.length === 0 || creatorSubmitting}
              test="test-request-interaction"
            />
            <Input
              label="Requester platform/user ID"
              value={creatorForm.requester_id}
              onChange={(requester_id) => onCreatorFormChange({ ...creatorForm, requester_id })}
              required
              test="test-request-requester-id"
            />
            <Input
              label="Display name"
              value={creatorForm.requester_display_name}
              onChange={(requester_display_name) => onCreatorFormChange({ ...creatorForm, requester_display_name })}
              test="test-request-display-name"
            />
            <Input
              label="Username"
              value={creatorForm.requester_username}
              onChange={(requester_username) => onCreatorFormChange({ ...creatorForm, requester_username })}
              test="test-request-username"
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-end">
            <Input
              label="Note/message"
              value={creatorForm.note}
              onChange={(note) => onCreatorFormChange({ ...creatorForm, note })}
              test="test-request-note"
            />
            <button
              type="submit"
              disabled={creatorSubmitting || definitions.length === 0 || !creatorForm.interaction_id || !creatorForm.requester_id.trim()}
              className="inline-flex justify-center items-center gap-2 h-9 px-4 rounded bg-[hsl(var(--primary))] text-white text-sm disabled:opacity-50"
              data-testid="create-test-interaction-request"
            >
              <Plus className="w-4 h-4" /> {creatorSubmitting ? "Creating..." : "Create test request"}
            </button>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Optional display name, username, and note are stored inside supported input values for local testing only.
          </p>
          {definitions.length === 0 && (
            <div className="rounded border border-amber-400/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
              No interaction definitions are loaded yet. Create an interaction above before making test requests.
            </div>
          )}
          {creatorMessage && <div className="rounded border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100">{creatorMessage}</div>}
          {creatorError && <div className="rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-red-100">{creatorError}</div>}
        </form>
      </div>

      <div className="p-5 border-b border-[hsl(var(--border))] flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <SectionHeader
          eyebrow="Local/dev admin tooling"
          title="Interaction Request Queue"
          description="Local/dev admin queue viewer. This is not a public viewer redeem UI."
          icon={Sparkles}
        />
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--primary))] disabled:opacity-50"
          data-testid="refresh-interaction-request-queue"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="m-5 rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-red-100" data-testid="interaction-request-queue-error">
          {error}
        </div>
      )}

      {loading && requests.length === 0 ? (
        <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading interaction request queue...</div>
      ) : requests.length === 0 ? (
        <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]" data-testid="interaction-request-queue-empty">
          No queued interaction requests.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              <tr className="border-b border-[hsl(var(--border))]">
                <th className="py-3 px-4">Request</th>
                <th>Status</th>
                <th>Interaction</th>
                <th>Category / Lane</th>
                <th>Requester</th>
                <th>Approval</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Error</th>
                <th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))]" data-testid={`interaction-request-${request.id}`}>
                  <td className="py-3 px-4 align-top">
                    <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] break-all">{request.id}</div>
                    {request.queue_position > 0 && <div className="mt-1 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Queue #{request.queue_position}</div>}
                  </td>
                  <td className="align-top">
                    <StatusPill status={request.status} />
                  </td>
                  <td className="align-top">
                    <div className="font-medium">{request.interaction_name || request.interaction_id || "Unknown interaction"}</div>
                    {request.interaction_id && <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))] break-all">{request.interaction_id}</div>}
                  </td>
                  <td className="align-top">
                    <div>{request.interaction_category || (request.category_tags || []).join(", ") || "-"}</div>
                    <code className="text-xs text-[hsl(var(--muted-foreground))]">{request.queue_type || "-"}</code>
                  </td>
                  <td className="align-top">
                    <RequesterIdentity request={request} />
                  </td>
                  <td className="align-top">{request.approval_required ? "Required" : "Not required"}</td>
                  <td className="align-top text-xs text-[hsl(var(--muted-foreground))]">{formatDate(request.created_at)}</td>
                  <td className="align-top text-xs text-[hsl(var(--muted-foreground))]">{formatDate(request.updated_at)}</td>
                  <td className="align-top text-xs text-[hsl(var(--destructive))] max-w-[180px]">
                    {request.error_message || request.failure_message || request.rejection_reason || "-"}
                  </td>
                  <td className="align-top text-right pr-4">
                    {request.status === "pending_approval" ? (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onApprove(request)}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`approve-interaction-request-${request.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => onReject(request)}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
                          data-testid={`reject-interaction-request-${request.id}`}
                        >
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StatusPill({ status }) {
  const color = status === "pending_approval"
    ? "border-amber-400 text-amber-300 bg-amber-500/10"
    : status === "pending"
      ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
      : status === "running"
        ? "border-emerald-500 text-emerald-300 bg-emerald-500/10"
        : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]";
  return <span className={`inline-flex px-2 py-1 rounded border text-xs font-mono ${color}`}>{status || "unknown"}</span>;
}

function RequesterIdentity({ request }) {
  const inputValues = request.input_values || {};
  const identity = getRequesterIdentity(request);
  return (
    <div>
      <div className="font-medium">{identity.primary}</div>
      {identity.pronouns && <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">{identity.pronouns}</div>}
      {identity.debug && <div className="mt-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))] break-all">{identity.debug}</div>}
      {inputValues.note && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))] break-words">{inputValues.note}</div>}
      {request.source && <div className="mt-1 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{request.source}</div>}
    </div>
  );
}

function getRequesterIdentity(request) {
  const inputValues = request.input_values || {};
  const preferredName = request.requester_preferred_name || request.preferred_name || inputValues.requester_preferred_name;
  const pronouns = request.requester_pronouns || request.pronouns || inputValues.requester_pronouns;
  const displayName = request.requester_display_name || request.display_name || inputValues.requester_display_name;
  const username = request.requester_login || request.requester_username || request.username || request.login || inputValues.requester_username;
  const id = request.requester_id || request.requester_twitch_user_id || request.twitch_user_id || request.user_id;
  const visibleAccount = username ? `@${username}` : displayName || id || "";

  if (preferredName) {
    return {
      primary: visibleAccount ? `${preferredName} (${visibleAccount})` : preferredName,
      pronouns,
      debug: id && id !== username ? `requester_id: ${id}` : "",
    };
  }

  if (displayName && username && displayName !== username) {
    return {
      primary: `${displayName} (@${username})`,
      pronouns,
      debug: id && id !== username ? `requester_id: ${id}` : "",
    };
  }

  if (username || displayName) {
    return {
      primary: username || displayName,
      pronouns,
      debug: id && id !== username && id !== displayName ? `requester_id: ${id}` : "",
    };
  }

  return {
    primary: "Unknown requester",
    pronouns,
    debug: id ? `requester_id: ${id}` : "",
  };
}

function RuntimeSimulatorPanel({
  pending,
  claimed,
  loading,
  error,
  failureMessage,
  onFailureMessageChange,
  onRefresh,
  onClaim,
  onComplete,
  onFail,
}) {
  return (
    <section className="surface overflow-hidden" data-testid="runtime-simulator-panel">
      <div className="p-5 border-b border-[hsl(var(--border))] flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <SectionHeader
          eyebrow="Local/dev only"
          title="Local/Dev Runtime Simulator"
          description="Claims and reports interaction request results without executing OBS, media, TTS, or overlay behavior."
          icon={Play}
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-50"
            data-testid="refresh-runtime-pending"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={onClaim}
            disabled={loading}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-50"
            data-testid="claim-next-runtime-request"
          >
            <Play className="w-3.5 h-3.5" /> Claim Next
          </button>
        </div>
      </div>

      {error && (
        <div className="m-5 rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-red-100" data-testid="runtime-simulator-error">
          {error}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-medium">Pending executable requests</h3>
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{pending.length} pending</span>
          </div>
          {pending.length === 0 ? (
            <div className="border border-dashed border-[hsl(var(--border))] rounded p-4 text-sm text-[hsl(var(--muted-foreground))]">
              No pending runtime requests.
            </div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))] border border-[hsl(var(--border))] rounded overflow-hidden">
              {pending.slice(0, 8).map((request) => (
                <div key={request.id} className="p-3 bg-[hsl(var(--surface-2))]">
                  <RuntimeRequestSummary request={request} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h3 className="font-medium">Claimed / Running Request</h3>
          {!claimed ? (
            <div className="border border-dashed border-[hsl(var(--border))] rounded p-4 text-sm text-[hsl(var(--muted-foreground))]">
              No request claimed in this simulator panel.
            </div>
          ) : (
            <div className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface-2))] p-4 space-y-4">
              <RuntimeRequestSummary request={claimed} />
              <RuntimeActionList actions={claimed.actions || []} />
              <RuntimeResultDetails request={claimed} />
              {claimed.status === "running" ? (
                <>
                  <div>
                    <div className="label-eyebrow mb-1.5">Failure/error message</div>
                    <input
                      value={failureMessage}
                      onChange={(e) => onFailureMessageChange(e.target.value)}
                      className="w-full h-9 bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm"
                      data-testid="runtime-failure-message"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={onComplete}
                      disabled={loading}
                      className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                      data-testid="complete-runtime-request"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Mark Completed
                    </button>
                    <button
                      type="button"
                      onClick={onFail}
                      disabled={loading}
                      className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10 disabled:opacity-50"
                      data-testid="fail-runtime-request"
                    >
                      <X className="w-4 h-4" /> Mark Failed
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">This request is no longer running.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function RuntimeResultDetails({ request }) {
  const metadata = request.runtime_result_metadata;
  const actionResults = Array.isArray(metadata?.action_results) ? metadata.action_results : [];
  const outputs = runtimeOutputItems(metadata);
  const audioReferences = runtimeAudioReferenceItems(metadata);
  const hasMetadata = metadata && Object.keys(metadata).length > 0;
  const hasError = Boolean(request.runtime_error_message);

  if (!hasMetadata && !hasError && actionResults.length === 0 && outputs.length === 0 && audioReferences.length === 0) {
    return (
      <div className="border border-dashed border-[hsl(var(--border))] rounded p-3 text-sm text-[hsl(var(--muted-foreground))]">
        Runtime result metadata will appear here after this local/dev request completes or fails.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {hasError && (
        <div>
          <div className="label-eyebrow mb-2">Runtime Error Message</div>
          <div className="rounded border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-red-100 break-words" data-testid="runtime-result-error-message">
            {request.runtime_error_message}
          </div>
        </div>
      )}

      {audioReferences.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2">Generated Audio References (Inspection Only)</div>
          <div className="space-y-2">
            {audioReferences.map((item, index) => (
              <RuntimeAudioReferenceCard key={`${item.label}-${item.value}-${index}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {outputs.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2">Runtime Outputs</div>
          <div className="space-y-2">
            {outputs.map((item, index) => (
              <div key={`${item.label}-${index}`} className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{item.label}</div>
                <pre className="text-xs whitespace-pre-wrap break-words text-[hsl(var(--foreground))]">{formatRuntimeValue(item.value)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {actionResults.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2">Runtime Outputs / Action Results</div>
          <div className="space-y-2">
            {actionResults.map((result, index) => (
              <div key={`${result.action_id || "action"}-${index}`} className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium">{index + 1}. {result.action_type || "unknown"}</div>
                  <StatusPill status={result.status || "unknown"} />
                </div>
                {result.error_message && <div className="mt-2 text-sm text-[hsl(var(--destructive))] break-words">{result.error_message}</div>}
                {result.message && <div className="mt-2 text-sm break-words">{result.message}</div>}
                <RuntimeJsonBlock value={result} />
              </div>
            ))}
          </div>
        </div>
      )}

      {hasMetadata && (
        <div>
          <div className="label-eyebrow mb-2">Runtime Result Metadata</div>
          <RuntimeJsonBlock value={metadata} testId="runtime-result-metadata" />
        </div>
      )}
    </div>
  );
}

function RuntimeAudioReferenceCard({ item }) {
  const [audioError, setAudioError] = useState("");
  const href = runtimeAudioHref(item.value);

  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3">
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">{item.label}</div>
      <div className="font-mono text-xs break-all text-[hsl(var(--foreground))]">{item.value}</div>
      {href && (
        <div className="mt-3 space-y-2">
          <audio
            controls
            preload="none"
            src={href}
            onError={() => setAudioError("Could not load this audio reference. The file may be missing or unavailable.")}
            onCanPlay={() => setAudioError("")}
            className="w-full"
          />
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-xs text-[hsl(var(--primary))] hover:underline"
          >
            Open audio reference
          </a>
          {audioError && <div className="text-xs text-[hsl(var(--destructive))]">{audioError}</div>}
        </div>
      )}
    </div>
  );
}

function RuntimeJsonBlock({ value, testId }) {
  return (
    <pre
      className="mt-2 max-h-64 overflow-auto rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 text-[11px] whitespace-pre-wrap break-words text-[hsl(var(--muted-foreground))]"
      data-testid={testId}
    >
      {JSON.stringify(value || {}, null, 2)}
    </pre>
  );
}

function runtimeOutputItems(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  const keys = ["runtime_outputs", "outputs", "output", "ai_output", "tts_output", "response_text", "generated_text"];
  const items = [];
  keys.forEach((key) => {
    if (metadata[key] !== undefined && metadata[key] !== null && metadata[key] !== "") {
      items.push({ label: key.replaceAll("_", " "), value: metadata[key] });
    }
  });
  (Array.isArray(metadata.action_results) ? metadata.action_results : []).forEach((result, index) => {
    const resultMetadata = result?.metadata;
    if (!resultMetadata || typeof resultMetadata !== "object") return;
    keys.forEach((key) => {
      if (resultMetadata[key] !== undefined && resultMetadata[key] !== null && resultMetadata[key] !== "") {
        items.push({ label: `action ${index + 1} ${key.replaceAll("_", " ")}`, value: resultMetadata[key] });
      }
    });
  });
  return items;
}

function runtimeAudioReferenceItems(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  const audioKeys = new Set(["audio_reference", "audio_url", "output_audio_reference", "generated_audio_reference"]);
  const items = [];
  const seen = new Set();

  const addReference = (label, value) => {
    const text = String(value || "").trim();
    if (!text || seen.has(`${label}:${text}`)) return;
    seen.add(`${label}:${text}`);
    items.push({ label: label.replaceAll("_", " "), value: text });
  };

  const visit = (value, label = "runtime metadata") => {
    if (typeof value === "string") {
      if (value.includes("/api/tts/")) addReference(label, value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, `${label} ${index + 1}`));
      return;
    }
    if (!value || typeof value !== "object") return;
    Object.entries(value).forEach(([key, nestedValue]) => {
      if (audioKeys.has(key) && typeof nestedValue === "string") {
        addReference(key, nestedValue);
      }
      visit(nestedValue, key);
    });
  };

  visit(metadata);
  return items;
}

function runtimeAudioHref(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.startsWith("/api/")) return `${API}${text.slice(4)}`;
  if (text.startsWith("http://") || text.startsWith("https://") || text.startsWith("/")) return text;
  return "";
}

function formatRuntimeValue(value) {
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
}

function RuntimeRequestSummary({ request }) {
  const requester = getRequesterIdentity(request);
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">{request.interaction_name || request.interaction_id || "Unknown interaction"}</div>
          <div className="font-mono text-[10px] text-[hsl(var(--muted-foreground))] break-all">{request.id}</div>
        </div>
        <StatusPill status={request.status} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <div><span className="text-white">Lane:</span> {request.queue_type || "-"}</div>
        <div><span className="text-white">Queue:</span> {request.queue_position || "-"}</div>
        <div className="col-span-2">
          <span className="text-white">Requester:</span> {requester.primary}
          {requester.pronouns && <div className="mt-0.5">{requester.pronouns}</div>}
          {requester.debug && <div className="mt-0.5 font-mono break-all">{requester.debug}</div>}
        </div>
        <div><span className="text-white">Cost:</span> {request.cost_amount || 0} {currencyLabel({ id: request.cost_type })}</div>
        <div><span className="text-white">Created:</span> {formatDate(request.created_at)}</div>
        <div><span className="text-white">Updated:</span> {formatDate(request.updated_at)}</div>
      </div>
    </div>
  );
}

function RuntimeActionList({ actions }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">Action metadata</div>
      {actions.length === 0 ? (
        <div className="text-sm text-[hsl(var(--muted-foreground))]">No action metadata on this request.</div>
      ) : (
        <div className="space-y-2">
          {actions.map((action, index) => (
            <div key={action.id || index} className="border border-[hsl(var(--border))] rounded p-3 bg-[hsl(var(--surface))]">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">{index + 1}. {action.action_type || "unknown"}</div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{action.enabled === false ? "disabled" : "enabled"}</span>
              </div>
              <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words text-[hsl(var(--muted-foreground))]">{JSON.stringify(action.config || {}, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QueueLane({ queue, label = "", running, requests, currencyMap, onCancel, onMove, onProcessNext, onCompleteRunning, onRunActions } = {}) {
  return (
    <div className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface-2))] overflow-hidden" data-testid={`redeem-queue-${queue}`}>
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between gap-3">
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">{running ? "1 running" : "0 running"} - {requests.length} pending</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onProcessNext(queue)}
            disabled={!!running || requests.length === 0}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`process-next-${queue}`}
          >
            <Play className="w-3.5 h-3.5" /> Process Next
          </button>
          {running && (
            <button
              type="button"
              onClick={() => onCompleteRunning(queue)}
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"
              data-testid={`complete-running-${queue}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Complete Running
            </button>
          )}
          <code className="text-xs text-[hsl(var(--muted-foreground))]">{queue}</code>
        </div>
      </div>
      <div className="divide-y divide-[hsl(var(--border))]">
        {running && (
          <div className="p-4 bg-emerald-500/5 border-b border-[hsl(var(--border))]" data-testid={`running-request-${queue}`}>
            <div className="label-eyebrow mb-2 text-emerald-300">Running</div>
            <RequestSummary request={running} currencyMap={currencyMap} />
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Started: {formatDate(running.started_at)}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onRunActions(running)}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                data-testid={`run-actions-${running.id}`}
              >
                <Play className="w-3.5 h-3.5" /> Run Actions
              </button>
            </div>
          </div>
        )}
        {requests.length === 0 && <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]">No pending requests.</div>}
        {requests.map((request, index) => {
          const isFirst = index === 0;
          const isLast = index === requests.length - 1;
          return (
            <div key={request.id} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <RequestSummary request={request} currencyMap={currencyMap} />
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Refunded: {request.refunded ? "Yes" : "No"}</div>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2">
                <div className="flex gap-1">
                  <IconButton icon={ChevronsUp} label="Move to top" disabled={isFirst} onClick={() => onMove(request, "top")} test={`move-top-request-${request.id}`} />
                  <IconButton icon={ArrowUp} label="Move up" disabled={isFirst} onClick={() => onMove(request, "up")} test={`move-up-request-${request.id}`} />
                  <IconButton icon={ArrowDown} label="Move down" disabled={isLast} onClick={() => onMove(request, "down")} test={`move-down-request-${request.id}`} />
                  <IconButton icon={ChevronsDown} label="Move to bottom" disabled={isLast} onClick={() => onMove(request, "bottom")} test={`move-bottom-request-${request.id}`} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => onCancel(request, false)} className="text-xs px-2 py-1 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white" data-testid={`cancel-request-${request.id}`}>Cancel</button>
                  <button onClick={() => onCancel(request, true)} className="text-xs px-2 py-1 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10" data-testid={`refund-request-${request.id}`}>Refund</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RequestSummary({ request, currencyMap }) {
  const currency = currencyMap[request.cost_type];
  return (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">#{request.queue_position || "-"}</span>
        <span className="font-medium truncate">{request.interaction_name}</span>
      </div>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
        {request.username} - {request.cost_amount} {currencyLabel(currency || { id: request.cost_type })} - {formatDate(request.created_at)}
      </div>
    </>
  );
}

function IconButton({ icon: Icon, label = "", disabled, onClick, test } = {}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={test}
      className="p-1.5 rounded border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function CategoryPicker({ categories, value, onChange }) {
  const selected = new Set(value || []);
  const toggle = (slug) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    onChange([...next]);
  };

  return (
    <div>
      <div className="label-eyebrow mb-2">Category tags</div>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Choose one or more categories.</p>
      <div className="flex flex-wrap gap-2" data-testid="interaction-category-tags">
        {categories.length === 0 && <span className="text-sm text-[hsl(var(--muted-foreground))]">Create categories above to tag interactions.</span>}
        {categories.map((category) => {
          const active = selected.has(category.slug);
          return (
            <button
              type="button"
              key={category.slug}
              onClick={() => toggle(category.slug)}
              disabled={category.enabled === false && !active}
              className="text-xs uppercase tracking-widest px-3 py-1.5 rounded border disabled:opacity-40"
              style={{
                borderColor: active ? category.color : "hsl(var(--border))",
                background: active ? `${category.color}22` : "transparent",
                color: active ? category.color : "hsl(var(--muted-foreground))",
              }}
              data-testid={`interaction-category-tag-${category.slug}`}
            >
              {category.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RewardAliasPicker({ label, help, value, options, placeholder, onChange, test }) {
  const aliases = parseAliasText(value);
  const [draft, setDraft] = useState("");
  const availableOptions = (options || []).filter((option) => !aliases.some((alias) => alias.toLowerCase() === option.toLowerCase()));
  const addAlias = (alias) => {
    const parsed = parseAliasText(alias);
    if (parsed.length === 0) return;
    onChange(parseAliasText([...aliases, ...parsed]));
    setDraft("");
  };
  const removeAlias = (alias) => {
    onChange(aliases.filter((item) => item !== alias));
  };

  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
        <DatalistInput
          label="Reward name"
          value={draft}
          onChange={setDraft}
          options={availableOptions.map((option) => [option, option])}
          placeholder={availableOptions.length > 0 ? "Choose or type reward name" : placeholder}
          test={`${test}-draft`}
        />
        <button
          type="button"
          onClick={() => addAlias(draft)}
          disabled={!draft.trim()}
          className="self-end h-9 rounded border border-[hsl(var(--border))] px-3 text-sm text-[hsl(var(--muted-foreground))] hover:text-white disabled:opacity-40"
          data-testid={`${test}-add`}
        >
          Add
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2" data-testid={test}>
        {aliases.length === 0 && <span className="text-xs text-[hsl(var(--muted-foreground))]">No platform reward names linked.</span>}
        {aliases.map((alias) => (
          <button
            type="button"
            key={alias}
            onClick={() => removeAlias(alias)}
            className="inline-flex items-center gap-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-2.5 py-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-white"
            title="Remove reward name"
          >
            {alias} <X className="w-3 h-3" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function ActionEditor({ actions, onChange }) {
  const [obsConfig, setObsConfig] = useState(defaultObsConfig);
  const [obsConfigLoaded, setObsConfigLoaded] = useState(false);
  const [obsSourcesByScene, setObsSourcesByScene] = useState({});
  const [obsFiltersBySource, setObsFiltersBySource] = useState({});
  const [obsLookupError, setObsLookupError] = useState("");
  const { connected: obsConnected, scenes: obsScenes, error: obsError, url: obsUrl, request: obsRequest, connect: connectObs } = useObs(obsConfig);
  const hasObsLookupAction = actions.some((action) => ["obs_scene", "obs_filter", "obs_filter_enable", "obs_filter_disable", "show_source", "hide_source", "toggle_source"].includes(action.action_type));

  useEffect(() => {
    setObsConfig(loadLocalObsConfig());
    setObsConfigLoaded(true);
  }, []);

  useEffect(() => {
    if (obsConfigLoaded && hasObsLookupAction) {
      connectObs();
    }
  }, [connectObs, hasObsLookupAction, obsConfigLoaded]);

  const obsStatusMessage = useMemo(() => {
    if (!hasObsLookupAction) return "";
    if (obsConnected) {
      if (obsScenes.length > 0) return `OBS connected. ${obsScenes.length} scene${obsScenes.length === 1 ? "" : "s"} available from this browser.`;
      return "OBS connected. No scenes were returned yet; you can still type names manually.";
    }
    return "OBS is not connected in this browser. Open OBS Control and connect first, or type names manually.";
  }, [hasObsLookupAction, obsConnected, obsScenes.length]);

  const addAction = () => onChange([...actions, makeAction()]);
  const updateAction = (index, patch) => {
    onChange(actions.map((action, i) => (i === index ? { ...action, ...patch } : action)));
  };
  const updateActionType = (index, action_type) => {
    updateAction(index, {
      action_type,
      config_text: JSON.stringify(defaultConfigForActionType(action_type), null, 2),
    });
  };
  const updateActionConfig = (index, patch) => {
    const action = actions[index];
    const current = parseActionConfig(action.config_text);
    updateAction(index, {
      config_text: JSON.stringify({ ...current, ...patch }, null, 2),
    });
  };
  const loadSceneSources = async (sceneName) => {
    if (!sceneName || obsSourcesByScene[sceneName]) return;
    if (!obsConnected) return;
    try {
      const data = await obsRequest("GetSceneItemList", { sceneName });
      const sources = await flattenObsSceneSources(data.sceneItems || [], obsRequest);
      setObsSourcesByScene((prev) => ({ ...prev, [sceneName]: sources }));
      setObsLookupError("");
    } catch (err) {
      setObsLookupError(err.message || "Could not load OBS sources.");
    }
  };
  const loadSourceFilters = async (sourceName) => {
    if (!sourceName || obsFiltersBySource[sourceName]) return;
    if (!obsConnected) return;
    try {
      const data = await obsRequest("GetSourceFilterList", { sourceName });
      const filters = (data.filters || []).map((filter) => filter.filterName).filter(Boolean);
      setObsFiltersBySource((prev) => ({ ...prev, [sourceName]: filters }));
      setObsLookupError("");
    } catch (err) {
      setObsLookupError(err.message || "Could not load OBS filters.");
    }
  };
  const removeAction = (index) => {
    onChange(actions.filter((_, i) => i !== index));
  };
  const moveAction = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= actions.length) return;
    const next = [...actions];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="label-eyebrow mb-1">Ordered Action Sequence</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))]">Actions run from top to bottom when you click Run Actions. Delay pauses before that step runs.</p>
        {obsStatusMessage && <p className={`text-xs mt-1 ${obsConnected ? "text-emerald-300" : "text-[hsl(var(--muted-foreground))]"}`}>{obsStatusMessage}</p>}
        {obsError && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{obsError}</p>}
        {obsUrl && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">OBS target: <code>{obsUrl}</code></p>}
        {obsLookupError && <p className="text-xs text-[hsl(var(--destructive))] mt-1">{obsLookupError}</p>}
      </div>

      {actions.length === 0 && (
        <div className="border border-dashed border-[hsl(var(--border))] rounded p-4 text-sm text-[hsl(var(--muted-foreground))]">No actions added.</div>
      )}

      <div className="space-y-3">
        {actions.map((action, index) => {
          const config = parseActionConfig(action.config_text);
          return (
          <div key={action.id} className="border border-[hsl(var(--border))] rounded p-3 bg-[hsl(var(--surface-2))] space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full border border-[hsl(var(--primary))] text-[hsl(var(--primary))] flex items-center justify-center text-xs font-mono shrink-0">{index + 1}</div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Step {index + 1} of {actions.length}</div>
                  <div className="text-sm font-medium truncate">{actionTypeLabel(action.action_type)}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => moveAction(index, -1)} disabled={index === 0} className="p-1.5 text-[hsl(var(--muted-foreground))] disabled:opacity-30 hover:text-white" title="Move up">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => moveAction(index, 1)} disabled={index === actions.length - 1} className="p-1.5 text-[hsl(var(--muted-foreground))] disabled:opacity-30 hover:text-white" title="Move down">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => removeAction(index)} className="p-1.5 text-[hsl(var(--destructive))]" title="Remove action">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <Select label="Action type" value={action.action_type} onChange={(v) => updateActionType(index, v)} options={actionTypeOptions} test={`interaction-action-type-${index}`} />
              <Number label="Pause before step (ms)" value={action.delay_ms} onChange={(v) => updateAction(index, { delay_ms: v })} test={`interaction-action-delay-${index}`} />
              <Toggle label="Enabled" checked={action.enabled !== false} onChange={(v) => updateAction(index, { enabled: v })} test={`interaction-action-enabled-${index}`} />
              <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono truncate">id: {action.id}</div>
            </div>
            <ActionConfigFields
              action={action}
              config={config}
              index={index}
              obsScenes={obsScenes}
              obsSources={obsSourcesByScene[config.scene_name] || []}
              obsFilters={obsFiltersBySource[config.source_name] || []}
              onConfigChange={(patch) => updateActionConfig(index, patch)}
              onSceneOpen={loadSceneSources}
              onSourceOpen={loadSourceFilters}
            />
            <details className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface))]">
              <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Advanced JSON</summary>
              <div className="px-3 pb-3">
                <textarea
                  value={action.config_text}
                  onChange={(e) => updateAction(index, { config_text: e.target.value })}
                  rows={4}
                  className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-xs font-mono"
                  data-testid={`interaction-action-config-${index}`}
                />
              </div>
            </details>
          </div>
        );})}
      </div>

      <button type="button" onClick={addAction} className="border border-[hsl(var(--border))] text-sm px-4 py-2 rounded flex items-center gap-2 hover:border-[hsl(var(--primary))]" data-testid="add-interaction-action">
        <Plus className="w-4 h-4" /> Add action
      </button>
    </div>
  );
}

function ActionConfigFields({
  action,
  config,
  index,
  obsScenes,
  obsSources,
  obsFilters,
  onConfigChange,
  onSceneOpen,
  onSourceOpen,
}) {
  const sceneOptions = (obsScenes || []).map((scene) => [scene, scene]);
  const sourceOptions = (obsSources || []).map((source) => [source, source]);
  const filterOptions = (obsFilters || []).map((filter) => [filter, filter]);

  if (action.action_type === "obs_scene") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ObsLookupField
          label="Scene name"
          value={config.scene_name || ""}
          onChange={(scene_name) => onConfigChange({ scene_name })}
          options={sceneOptions}
          placeholder="Type or select scene"
          test={`interaction-action-scene-name-${index}`}
        />
      </div>
    );
  }

  if (["obs_filter", "obs_filter_enable", "obs_filter_disable"].includes(action.action_type)) {
    const fixedEnabled = action.action_type === "obs_filter_enable" ? true : action.action_type === "obs_filter_disable" ? false : null;
    return (
      <div className={`grid grid-cols-1 ${fixedEnabled === null ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 items-end`}>
        <ObsLookupField
          label="Scene"
          value={config.scene_name || ""}
          onChange={(scene_name) => {
            onConfigChange({ scene_name, source_name: "", filter_name: "" });
            onSceneOpen(scene_name);
          }}
          onBlur={() => onSceneOpen(config.scene_name || "")}
          options={sceneOptions}
          placeholder="Type or select scene"
          test={`interaction-action-filter-scene-${index}`}
        />
        <ObsLookupField
          label="Source"
          value={config.source_name || ""}
          onChange={(source_name) => {
            onConfigChange({ source_name, filter_name: "" });
            onSourceOpen(source_name);
          }}
          onFocus={() => onSceneOpen(config.scene_name || "")}
          onBlur={() => onSourceOpen(config.source_name || "")}
          options={sourceOptions}
          placeholder="Type or select source"
          disabled={!config.scene_name}
          test={`interaction-action-source-name-${index}`}
        />
        <ObsLookupField
          label="Filter"
          value={config.filter_name || ""}
          onChange={(filter_name) => onConfigChange({ filter_name })}
          onFocus={() => onSourceOpen(config.source_name || "")}
          options={filterOptions}
          placeholder="Type or select filter"
          disabled={!config.source_name}
          test={`interaction-action-filter-name-${index}`}
        />
        {fixedEnabled === null ? (
          <Select
            label="Filter visibility"
            value={config.enabled === false ? "false" : "true"}
            onChange={(value) => onConfigChange({ enabled: value === "true" })}
            options={[["true", "Visible / on"], ["false", "Hidden / off"]]}
            test={`interaction-action-filter-enabled-${index}`}
          />
        ) : (
          <div className="text-xs text-[hsl(var(--muted-foreground))] pb-2">
            This step will turn the filter {fixedEnabled ? "on" : "off"}.
          </div>
        )}
      </div>
    );
  }

  if (["show_source", "hide_source", "toggle_source"].includes(action.action_type)) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ObsLookupField
          label="Scene"
          value={config.scene_name || ""}
          onChange={(scene_name) => {
            onConfigChange({ scene_name, source_name: "" });
            onSceneOpen(scene_name);
          }}
          onBlur={() => onSceneOpen(config.scene_name || "")}
          options={sceneOptions}
          placeholder="Type or select scene"
          test={`interaction-action-visibility-scene-${index}`}
        />
        <ObsLookupField
          label="Source"
          value={config.source_name || ""}
          onChange={(source_name) => onConfigChange({ source_name })}
          onFocus={() => onSceneOpen(config.scene_name || "")}
          options={sourceOptions}
          placeholder="Type or select source"
          disabled={!config.scene_name}
          test={`interaction-action-visibility-source-${index}`}
        />
      </div>
    );
  }

  if (action.action_type === "show_video_media" || action.action_type === "play_video") {
    return <VideoPlacementEditor config={config} index={index} onConfigChange={onConfigChange} />;
  }

  if (action.action_type === "clear_video_media") {
    return (
      <div className="border border-dashed border-[hsl(var(--border))] rounded p-3 text-xs text-[hsl(var(--muted-foreground))]">
        Clears the current video media placement metadata in local/dev runtime. This does not stop OBS or browser playback.
      </div>
    );
  }

  if (action.action_type === "twitch_create_clip") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
        <Input
          label="Clip title"
          help="Optional. Leave blank to let Twitch use the default title."
          value={config.title || ""}
          onChange={(title) => onConfigChange({ title })}
          test={`interaction-action-clip-title-${index}`}
        />
        <Number
          label="Duration seconds"
          help="Twitch allows 5 to 60 seconds."
          value={config.duration ?? 30}
          onChange={(duration) => onConfigChange({ duration })}
          test={`interaction-action-clip-duration-${index}`}
        />
      </div>
    );
  }

  if (action.action_type === "vip_assign") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="VIP source label"
          help="Use steal_vip or viewer_redeem for channel point steals (evict oldest VIP, then grant redeemer). Other labels use plain assign."
          value={config.source || "steal_vip"}
          onChange={(source) => onConfigChange({ source })}
          test={`interaction-action-vip-source-${index}`}
        />
      </div>
    );
  }

  if (action.action_type === "delay") {
    return (
      <div className="max-w-xs">
        <Number label="Delay seconds" value={config.delay_seconds ?? 1} onChange={(delay_seconds) => onConfigChange({ delay_seconds })} test={`interaction-action-delay-seconds-${index}`} />
      </div>
    );
  }

  if (action.action_type === "log_message") {
    return (
      <TextAreaInput
        label="Message"
        value={config.message || ""}
        onChange={(message) => onConfigChange({ message })}
        rows={2}
        test={`interaction-action-log-message-${index}`}
      />
    );
  }

  if (action.action_type === "ai_generate_text") {
    const promptMode = config.prompt_mode || (config.requires_viewer_input ? "viewer_topic" : "custom");
    const setPromptMode = (mode) => {
      if (mode === "viewer_topic") {
        onConfigChange({
          prompt_mode: mode,
          requires_viewer_input: true,
          viewer_input_label: config.viewer_input_label || "Topic",
          prompt_template: config.prompt_template || "Write a short, stream-safe random poem about {viewer_input}. Keep it under 8 lines.",
          output_key: config.output_key || "generated_text",
        });
      } else {
        onConfigChange({
          prompt_mode: mode,
          requires_viewer_input: false,
          prompt_template: config.prompt_template || "",
        });
      }
    };
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="AI input"
            help="Choose whether the viewer supplies a topic/message for the AI prompt."
            value={promptMode}
            onChange={setPromptMode}
            options={[
              ["viewer_topic", "Viewer gives a topic"],
              ["custom", "No viewer input"],
            ]}
            test={`interaction-action-ai-input-mode-${index}`}
          />
          <Input
            label="Save generated text as"
            help="The TTS step can read this key. Keep generated_text for the default flow."
            value={config.output_key || "generated_text"}
            onChange={(output_key) => onConfigChange({ output_key })}
            test={`interaction-action-ai-output-key-${index}`}
          />
          <Number
            label="Max text length"
            help="Limits how much generated text is saved and spoken."
            value={config.max_output_length ?? 500}
            onChange={(max_output_length) => onConfigChange({ max_output_length })}
            test={`interaction-action-ai-max-output-${index}`}
          />
        </div>
        {promptMode === "viewer_topic" && (
          <Input
            label="Viewer input label"
            help="This is the label shown on the redeem page."
            value={config.viewer_input_label || "Topic"}
            onChange={(viewer_input_label) => onConfigChange({ viewer_input_label, requires_viewer_input: true })}
            test={`interaction-action-ai-viewer-input-label-${index}`}
          />
        )}
        <TextAreaInput
          label={promptMode === "viewer_topic" ? "AI prompt using viewer input" : "AI prompt"}
          help={promptMode === "viewer_topic" ? "Use {viewer_input} or {topic} where the viewer's topic should appear." : "Prompt sent to the AI provider."}
          value={config.prompt_template || ""}
          onChange={(prompt_template) => onConfigChange({ prompt_template })}
          rows={4}
          test={`interaction-action-ai-prompt-${index}`}
        />
        <details className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface))]">
          <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Advanced AI</summary>
          <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Number label="Max tokens" help="Provider token limit. Usually leave this alone." value={config.max_tokens ?? 150} onChange={(max_tokens) => onConfigChange({ max_tokens })} test={`interaction-action-ai-max-tokens-${index}`} />
            <Input label="Safety mode" help="Internal safety profile label." value={config.safety_mode || "stream_safe"} onChange={(safety_mode) => onConfigChange({ safety_mode })} test={`interaction-action-ai-safety-mode-${index}`} />
            <Input label="Fallback text" help="Optional backup text for future failure handling." value={config.fallback_text || ""} onChange={(fallback_text) => onConfigChange({ fallback_text })} test={`interaction-action-ai-fallback-${index}`} />
          </div>
        </details>
      </div>
    );
  }

  if (action.action_type === "tts_generate_audio") {
    const textMode = config.text_mode || (config.literal_text ? "fixed_text" : (["viewer_input", "user_input", "requester_input"].includes(config.input_text_key) ? "user_input" : "generated_text"));
    const setTextMode = (mode) => {
      if (mode === "user_input") {
        onConfigChange({ text_mode: mode, input_text_key: "viewer_input", literal_text: "" });
      } else if (mode === "generated_text") {
        onConfigChange({ text_mode: mode, input_text_key: config.input_text_key && !["viewer_input", "user_input", "requester_input"].includes(config.input_text_key) ? config.input_text_key : "generated_text", literal_text: "" });
      } else {
        onConfigChange({ text_mode: mode, input_text_key: "", literal_text: config.literal_text || "" });
      }
    };
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="What should be spoken?"
            help="Choose whether TTS reads viewer text, generated AI text, or fixed text you type here."
            value={textMode}
            onChange={setTextMode}
            options={[
              ["user_input", "Viewer/user input"],
              ["generated_text", "Generated text"],
              ["fixed_text", "Fixed text"],
            ]}
            test={`interaction-action-tts-audio-mode-${index}`}
          />
          <Input
            label="ElevenLabs voice id"
            help="The ElevenLabs voice id to use for this TTS audio."
            value={config.voice_id || ""}
            onChange={(voice_id) => onConfigChange({ voice_id })}
            test={`interaction-action-tts-audio-voice-id-${index}`}
          />
          <Number
            label="Max characters"
            help="Safety limit for how much text TTS can read."
            value={config.max_character_length ?? 280}
            onChange={(max_character_length) => onConfigChange({ max_character_length })}
            test={`interaction-action-tts-audio-max-length-${index}`}
          />
        </div>
        {textMode === "user_input" && (
          <div className="rounded border border-[hsl(var(--border))] bg-black/20 px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
            Viewers will get a message box on the redeem page. Their text becomes the TTS audio.
          </div>
        )}
        {textMode === "generated_text" && (
          <Input
            label="Generated text key"
            help="Use the output key from an earlier AI Generate Text action. The default is generated_text."
            value={config.input_text_key || "generated_text"}
            onChange={(input_text_key) => onConfigChange({ input_text_key })}
            test={`interaction-action-tts-audio-input-key-${index}`}
          />
        )}
        {textMode === "fixed_text" && (
          <TextAreaInput
            label="Text to speak"
            help="Use this when you want TTS to always say the same thing."
            value={config.literal_text || ""}
            onChange={(literal_text) => onConfigChange({ literal_text })}
            rows={2}
            test={`interaction-action-tts-audio-literal-text-${index}`}
          />
        )}
        <details className="border border-[hsl(var(--border))] rounded bg-[hsl(var(--surface))]">
          <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Advanced TTS</summary>
          <div className="px-3 pb-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Output key" help="Where this generated audio reference is saved for later actions." value={config.output_key || "tts_audio"} onChange={(output_key) => onConfigChange({ output_key })} test={`interaction-action-tts-audio-output-key-${index}`} />
              <Input label="Model ID" help="Optional ElevenLabs model id. Leave blank unless you need a specific model." value={config.model_id || ""} onChange={(model_id) => onConfigChange({ model_id })} test={`interaction-action-tts-audio-model-id-${index}`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Range label="Stability" help="Lower is more expressive, higher is more consistent." value={config.stability ?? 0.5} onChange={(stability) => onConfigChange({ stability })} test={`interaction-action-tts-audio-stability-${index}`} />
              <Range label="Similarity" help="How closely ElevenLabs should match the selected voice." value={config.similarity_boost ?? 0.75} onChange={(similarity_boost) => onConfigChange({ similarity_boost })} test={`interaction-action-tts-audio-similarity-${index}`} />
            </div>
            <Toggle
              label="Save generated audio to sound library"
              help="Copies the generated MP3 into the normal sound library so it can be reused later."
              checked={config.save_to_sound_library === true}
              onChange={(save_to_sound_library) => onConfigChange({ save_to_sound_library })}
              test={`interaction-action-tts-save-sound-${index}`}
            />
            {config.save_to_sound_library === true && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="Saved sound name" help="Optional name for the reusable sound entry." value={config.sound_name || ""} onChange={(sound_name) => onConfigChange({ sound_name })} test={`interaction-action-tts-sound-name-${index}`} />
                <Input label="Saved sound key" help="Optional chat/redeem key. If blank, one is generated from the name." value={config.sound_key || ""} onChange={(sound_key) => onConfigChange({ sound_key })} test={`interaction-action-tts-sound-key-${index}`} />
              </div>
            )}
          </div>
        </details>
      </div>
    );
  }

  if (action.action_type === "play_sound") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Sound reference" value={config.media_reference || config.sound_reference || ""} onChange={(media_reference) => onConfigChange({ media_reference })} test={`interaction-action-sound-reference-${index}`} />
        <Number label="Volume" value={config.volume ?? 100} onChange={(volume) => onConfigChange({ volume })} test={`interaction-action-sound-volume-${index}`} />
        <Number label="Duration seconds" value={config.duration ?? 0} onChange={(duration) => onConfigChange({ duration })} test={`interaction-action-sound-duration-${index}`} />
      </div>
    );
  }

  if (action.action_type === "tts") {
    return (
      <div className="space-y-3">
        <div className="rounded border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm text-amber-100">
          This is the older TTS action. Use Generate TTS audio for actual ElevenLabs audio generation.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Voice" help="Legacy voice label. Prefer ElevenLabs voice id in Generate TTS audio." value={config.voice || ""} onChange={(voice) => onConfigChange({ voice })} test={`interaction-action-tts-voice-${index}`} />
          <Number label="Max characters" help="Safety limit for viewer/user text." value={config.max_character_length ?? 280} onChange={(max_character_length) => onConfigChange({ max_character_length })} test={`interaction-action-tts-max-length-${index}`} />
          <Select
            label="Text source"
            help="Choose whether this legacy action reads viewer input or generated text."
            value={config.text_source || "requester_input"}
            onChange={(text_source) => onConfigChange({ text_source })}
            options={[
              ["requester_input", "Viewer/user input"],
              ["generated_text", "Generated text"],
            ]}
            test={`interaction-action-tts-text-source-${index}`}
          />
        </div>
      </div>
    );
  }

  if (action.action_type === "chat_message") {
    return (
      <TextAreaInput
        label="Chat message"
        value={config.message || ""}
        onChange={(message) => onConfigChange({ message })}
        rows={2}
        test={`interaction-action-chat-message-${index}`}
      />
    );
  }

  if (action.action_type === "rpg_event") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Event key" value={config.event_key || ""} onChange={(event_key) => onConfigChange({ event_key })} test={`interaction-action-rpg-event-key-${index}`} />
        <Input label="Target" value={config.target || ""} onChange={(target) => onConfigChange({ target })} test={`interaction-action-rpg-target-${index}`} />
        <Number label="Amount" value={config.amount ?? 1} onChange={(amount) => onConfigChange({ amount })} test={`interaction-action-rpg-amount-${index}`} />
      </div>
    );
  }

  if (action.action_type === "give_item") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Input label="Item ID" value={config.item_id || ""} onChange={(item_id) => onConfigChange({ item_id })} test={`interaction-action-item-id-${index}`} />
        <Number label="Quantity" value={config.quantity ?? 1} onChange={(quantity) => onConfigChange({ quantity })} test={`interaction-action-item-quantity-${index}`} />
        <Input label="Recipient source" value={config.recipient_source || "requester"} onChange={(recipient_source) => onConfigChange({ recipient_source })} test={`interaction-action-item-recipient-${index}`} />
      </div>
    );
  }

  if (action.action_type === "trigger_interaction") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Interaction ID" value={config.interaction_id || ""} onChange={(interaction_id) => onConfigChange({ interaction_id })} test={`interaction-action-trigger-id-${index}`} />
        <Toggle label="Use same requester" checked={config.use_same_requester !== false} onChange={(use_same_requester) => onConfigChange({ use_same_requester })} test={`interaction-action-trigger-same-requester-${index}`} />
      </div>
    );
  }

  if (action.action_type === "random_action") {
    return (
      <div className="space-y-3">
        <Input
          label="Action IDs"
          value={listToText(config.action_ids)}
          onChange={(value) => onConfigChange({ action_ids: textToList(value) })}
          test={`interaction-action-random-ids-${index}`}
        />
        <Select
          label="Selection mode"
          value={config.selection_mode || "one"}
          onChange={(selection_mode) => onConfigChange({ selection_mode })}
          options={[["one", "One action"], ["all", "All listed actions"]]}
          test={`interaction-action-random-mode-${index}`}
        />
      </div>
    );
  }

  return (
    <div className="border border-dashed border-[hsl(var(--border))] rounded p-3 text-xs text-[hsl(var(--muted-foreground))]">
      Use Advanced JSON for this action type.
    </div>
  );
}

function VideoPlacementEditor({ config, index, onConfigChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);
  const dimensionAppliedRef = useRef("");
  const placement = {
    x: numericConfigValue(config.x, 100),
    y: numericConfigValue(config.y, 100),
    width: numericConfigValue(config.width, 640),
    height: numericConfigValue(config.height, 360),
  };
  const boxStyle = {
    left: `${Math.max(0, Math.min(100, (placement.x / 1920) * 100))}%`,
    top: `${Math.max(0, Math.min(100, (placement.y / 1080) * 100))}%`,
    width: `${Math.max(1, Math.min(100, (placement.width / 1920) * 100))}%`,
    height: `${Math.max(1, Math.min(100, (placement.height / 1080) * 100))}%`,
  };
  const uploadVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/runtime-media/video/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onConfigChange({ media_type: "video", media_reference: data.media_reference, duration: null });
      toast.success("Video uploaded");
    } catch (err) {
      const message = videoUploadErrorMessage(err);
      setUploadError(message);
      toast.error(message);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
    }
  };
  const chromaEnabled = config.chroma_key_enabled === true;
  const mediaReference = config.media_reference || "";
  const uploadedName = mediaReference.replace(/^runtime-media\//, "");
  const mediaUrl = runtimeMediaUrl(mediaReference);

  useEffect(() => {
    dimensionAppliedRef.current = "";
  }, [mediaReference]);

  const applyVideoDimensions = (video) => {
    if (!mediaReference || dimensionAppliedRef.current === mediaReference) return;
    const fitted = fitVideoDimensions(video.videoWidth, video.videoHeight);
    if (!fitted) return;
    dimensionAppliedRef.current = mediaReference;
    if (placement.width !== fitted.width || placement.height !== fitted.height) {
      onConfigChange({ width: fitted.width, height: fitted.height });
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 space-y-3">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow mb-1.5">Uploaded video</div>
            <div className="min-h-9 rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] px-3 py-2 text-sm font-mono truncate" data-testid={`interaction-action-video-reference-${index}`}>
              {uploadedName || "No video uploaded yet"}
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              The overlay plays the uploaded video until the video ends.
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              disabled={uploading}
              onChange={(event) => uploadVideo(event.target.files?.[0])}
              className="hidden"
              data-testid={`interaction-action-video-upload-${index}`}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="h-9 rounded bg-[hsl(var(--primary))] px-3 text-xs uppercase tracking-widest text-white disabled:opacity-50"
            >
              {uploading ? "Uploading..." : mediaReference ? "Replace video" : "Upload video"}
            </button>
          </div>
        </div>
        {uploadError && <div className="text-xs text-[hsl(var(--destructive))]">{uploadError}</div>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Number label="X" value={placement.x} onChange={(x) => onConfigChange({ x })} test={`interaction-action-video-x-${index}`} />
        <Number label="Y" value={placement.y} onChange={(y) => onConfigChange({ y })} test={`interaction-action-video-y-${index}`} />
        <Number label="Width" value={placement.width} onChange={(width) => onConfigChange({ width })} test={`interaction-action-video-width-${index}`} />
        <Number label="Height" value={placement.height} onChange={(height) => onConfigChange({ height })} test={`interaction-action-video-height-${index}`} />
      </div>
      <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="label-eyebrow">Chroma key</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Configures green-screen style playback metadata for the overlay.</p>
          </div>
          <Toggle
            label="Enabled"
            checked={chromaEnabled}
            onChange={(chroma_key_enabled) => onConfigChange({ chroma_key_enabled })}
            test={`interaction-action-video-chroma-enabled-${index}`}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label>
            <span className="label-eyebrow mb-1.5 block">Key color</span>
            <input
              type="color"
              value={config.chroma_key_color || "#00ff00"}
              onChange={(event) => onConfigChange({ chroma_key_color: event.target.value })}
              className="w-full h-9 bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded"
              data-testid={`interaction-action-video-chroma-color-${index}`}
            />
          </label>
          <Range
            label="Similarity"
            value={config.chroma_key_similarity ?? 0.35}
            onChange={(chroma_key_similarity) => onConfigChange({ chroma_key_similarity })}
            test={`interaction-action-video-chroma-similarity-${index}`}
          />
          <Range
            label="Smoothness"
            value={config.chroma_key_smoothness ?? 0.08}
            onChange={(chroma_key_smoothness) => onConfigChange({ chroma_key_smoothness })}
            test={`interaction-action-video-chroma-smoothness-${index}`}
          />
        </div>
      </div>
      <div>
        <div className="label-eyebrow mb-2">1920x1080 Placement Preview</div>
        <div className="relative w-full max-w-2xl aspect-video rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "10% 10%" }} />
          <div className="absolute border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/20 min-w-2 min-h-2 overflow-hidden" style={boxStyle}>
            {mediaUrl && (
              <video
                src={mediaUrl}
                controls
                muted
                playsInline
                onLoadedMetadata={(event) => applyVideoDimensions(event.currentTarget)}
                className="absolute inset-0 h-full w-full object-fill"
                data-testid={`interaction-action-video-preview-${index}`}
              />
            )}
            {!mediaUrl && (
              <div className="absolute inset-0 flex items-center justify-center px-2 text-center text-[10px] text-[hsl(var(--muted-foreground))]">
                Upload a video to preview placement and chroma key.
              </div>
            )}
            <div className="absolute left-1 top-1 text-[10px] font-mono text-white bg-black/40 px-1 rounded">
              {placement.width}x{placement.height}{chromaEnabled ? " - chroma" : ""}
            </div>
            {mediaUrl && chromaEnabled && (
              <div className="absolute right-1 top-1 rounded bg-black/70 px-2 py-1 text-[10px] uppercase tracking-widest text-emerald-200">
                Chroma key saved for overlay
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
          This preview uses the same placement box the overlay uses. Chroma key is applied on the stream overlay, not in this admin preview.
        </p>
      </div>
    </div>
  );
}

function runtimeMediaUrl(mediaReference) {
  const filename = (mediaReference || "").replace(/^runtime-media\//, "");
  if (!filename) return "";
  return `${API}/runtime-media/${encodeURIComponent(filename)}`;
}

function fitVideoDimensions(width, height) {
  if (!width || !height) return null;
  const scale = Math.min(1, 1920 / width, 1080 / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function numericConfigValue(value, fallback) {
  const parsed = globalThis.Number(value);
  return globalThis.Number.isFinite(parsed) ? parsed : fallback;
}

function textToList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function listToText(value) {
  return Array.isArray(value) ? value.join(", ") : String(value || "");
}

function SectionHeader({ eyebrow, title, description, icon: Icon }) {
  return (
    <div>
      <div className="label-eyebrow">{eyebrow}</div>
      <h2 className="text-lg font-semibold tracking-tight mt-1 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[hsl(var(--primary))]" />} {title}
      </h2>
      {description && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5">{description}</p>}
    </div>
  );
}

function cooldownLabel(item) {
  const seconds = item.cooldown_seconds || 0;
  const scope = item.cooldown_scope || "none";
  if (!seconds || scope === "none") return "none";
  return `${seconds}s ${scope.replace("_", " ")}`;
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
}

export function makeAction() {
  return {
    id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    action_type: "obs_scene",
    enabled: true,
    delay_ms: 0,
    config_text: JSON.stringify(defaultConfigForActionType("obs_scene"), null, 2),
  };
}

export function actionFromApi(action) {
  const actionType = action.action_type === "play_video" ? "show_video_media" : action.action_type;
  return {
    id: action.id || `action-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    action_type: actionType || "chat_message",
    enabled: action.enabled !== false,
    delay_ms: action.delay_ms || 0,
    config_text: JSON.stringify(action.config || {}, null, 2),
  };
}

function parseActionConfig(configText) {
  try {
    const config = JSON.parse(configText || "{}");
    return config && !Array.isArray(config) && typeof config === "object" ? config : {};
  } catch (err) {
    return {};
  }
}

function defaultConfigForActionType(actionType) {
  if (actionType === "obs_scene") return { scene_name: "" };
  if (["obs_filter", "obs_filter_enable", "obs_filter_disable"].includes(actionType)) return { scene_name: "", source_name: "", filter_name: "", enabled: actionType !== "obs_filter_disable" };
  if (["show_source", "hide_source", "toggle_source"].includes(actionType)) return { scene_name: "", source_name: "" };
  if (actionType === "show_video_media" || actionType === "play_video") return { media_type: "video", media_reference: "", x: 100, y: 100, width: 640, height: 360, duration: null, chroma_key_enabled: false, chroma_key_color: "#00ff00", chroma_key_similarity: 0.35, chroma_key_smoothness: 0.08 };
  if (actionType === "clear_video_media") return {};
  if (actionType === "delay") return { delay_seconds: 1 };
  if (actionType === "log_message") return { message: "" };
  if (actionType === "ai_generate_text") return { prompt_mode: "viewer_topic", requires_viewer_input: true, viewer_input_label: "Topic", prompt_template: "Write a short, stream-safe random poem about {viewer_input}. Keep it under 8 lines.", output_key: "generated_text", max_output_length: 500, max_tokens: 150, safety_mode: "stream_safe", fallback_text: "" };
  if (actionType === "tts_generate_audio") return { text_mode: "user_input", output_key: "tts_audio", voice_id: "", input_text_key: "viewer_input", literal_text: "", max_character_length: 280, model_id: "", stability: 0.5, similarity_boost: 0.75 };
  if (actionType === "twitch_create_clip") return { title: "", duration: 30 };
  if (actionType === "vip_assign") return { source: "steal_vip" };
  if (actionType === "play_sound") return { media_reference: "", volume: 100, duration: 0 };
  if (actionType === "tts") return { voice: "", text_source: "requester_input", max_character_length: 280 };
  if (actionType === "chat_message") return { message: "" };
  if (actionType === "rpg_event") return { event_key: "", target: "requester", amount: 1 };
  if (actionType === "give_item") return { item_id: "", quantity: 1, recipient_source: "requester" };
  if (actionType === "trigger_interaction") return { interaction_id: "", use_same_requester: true };
  if (actionType === "random_action") return { action_ids: [], selection_mode: "one" };
  return {};
}

function actionTypeLabel(actionType) {
  if (actionType === "tts") return "Legacy TTS";
  return actionTypeOptions.find(([value]) => value === actionType)?.[1] || actionType || "Action";
}

export function prepareActionsPayload(actions) {
  return (actions || []).map((action, index) => {
      let config = {};
      try {
        config = JSON.parse(action.config_text || "{}");
      } catch (err) {
        throw new Error(`Action ${index + 1} config must be valid JSON`);
      }
      if (!config || Array.isArray(config) || typeof config !== "object") {
        throw new Error(`Action ${index + 1} config must be a JSON object`);
      }
      return {
        id: action.id,
        action_type: action.action_type,
        enabled: action.enabled !== false,
        delay_ms: action.delay_ms || 0,
        config,
      };
    });
}

function prepareInteractionPayload(form) {
  return {
    ...form,
    platform_redeem_aliases: parseAliasText(form.platform_redeem_aliases || []),
    twitch_reward_aliases: parseAliasText(form.twitch_reward_aliases || []),
    kick_reward_aliases: parseAliasText(form.kick_reward_aliases || []),
    actions: prepareActionsPayload(form.actions),
  };
}

function parseAliasText(value) {
  const raw = Array.isArray(value) ? value.join("\n") : String(value || "");
  const seen = new Set();
  return raw.split(/[\n,]+/)
    .map((item) => item.trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function rewardAliasOptionsFromInteractions(interactions) {
  const twitch = [];
  const kick = [];
  const add = (list, aliases) => {
    parseAliasText(aliases).forEach((alias) => {
      if (!list.some((item) => item.toLowerCase() === alias.toLowerCase())) list.push(alias);
    });
  };
  (interactions || []).forEach((interaction) => {
    add(twitch, interaction.twitch_reward_aliases || interaction.platform_redeem_aliases);
    add(kick, interaction.kick_reward_aliases || interaction.platform_redeem_aliases);
  });
  return {
    twitch: twitch.sort((a, b) => a.localeCompare(b)),
    kick: kick.sort((a, b) => a.localeCompare(b)),
  };
}

function rewardAliasOptionsFromPlatformEvents(items) {
  const twitch = [];
  const kick = [];
  const add = (list, value) => {
    const alias = String(value || "").trim();
    if (!alias || list.some((item) => item.toLowerCase() === alias.toLowerCase())) return;
    list.push(alias);
  };
  (items || []).forEach((item) => {
    const metadata = item.metadata && typeof item.metadata === "object" ? item.metadata : {};
    const reward = metadata.reward && typeof metadata.reward === "object" ? metadata.reward : {};
    const rewardValues = [
      metadata.reward_title,
      metadata.reward_name,
      metadata.reward_id,
      reward.title,
      reward.name,
      reward.id,
    ];
    const platform = String(item.platform || item.source || "").toLowerCase();
    const eventType = String(item.type || "").toLowerCase();
    const target = platform.includes("kick") || eventType.includes("kick") ? kick : twitch;
    if (!eventType.includes("reward") && !eventType.includes("redemption") && !rewardValues.some(Boolean)) return;
    rewardValues.forEach((value) => add(target, value));
  });
  return {
    twitch: twitch.sort((a, b) => a.localeCompare(b)),
    kick: kick.sort((a, b) => a.localeCompare(b)),
  };
}

function mergeRewardAliasOptions(...groups) {
  const merged = { twitch: [], kick: [] };
  const add = (platform, value) => {
    const alias = String(value || "").trim();
    if (!alias || merged[platform].some((item) => item.toLowerCase() === alias.toLowerCase())) return;
    merged[platform].push(alias);
  };
  groups.forEach((group) => {
    (group?.twitch || []).forEach((value) => add("twitch", value));
    (group?.kick || []).forEach((value) => add("kick", value));
  });
  return {
    twitch: merged.twitch.sort((a, b) => a.localeCompare(b)),
    kick: merged.kick.sort((a, b) => a.localeCompare(b)),
  };
}

function viewerInputLabelForInteraction(interaction) {
  for (const action of interaction.actions || []) {
    const config = action.config || {};
    if (action.action_type === "ai_generate_text" && (config.requires_viewer_input === true || config.prompt_mode === "viewer_topic" || promptUsesViewerInput(config.prompt_template))) {
      return String(config.viewer_input_label || "Topic").trim() || "Topic";
    }
    if (action.action_type === "tts_generate_audio" && ["user_input", "viewer_input", "requester_input"].includes(config.text_mode || config.input_text_key)) {
      return "Message for TTS";
    }
    if (action.action_type === "tts" && ["requester_input", "viewer_input", "user_input"].includes(config.text_source)) {
      return "Message for TTS";
    }
  }
  return "";
}

function promptUsesViewerInput(promptTemplate) {
  return /\{\{?\s*(viewer_input|user_input|requester_input|topic)\s*\}?\}/i.test(String(promptTemplate || ""));
}

function videoUploadErrorMessage(err) {
  const status = err.response?.status;
  const detail = err.response?.data?.detail;
  if (detail) return detail;
  if (status === 401 || status === 403) return "Video upload failed because this session is not signed in as admin.";
  if (status === 413) return "Video upload failed because the server upload limit is too low for this file.";
  if (status) return `Video upload failed with server status ${status}.`;
  return "Video upload failed. Check that the backend is reachable and you are still signed in.";
}

function LabelWithTip({ label = "", help = "" } = {}) {
  return (
    <div className="label-eyebrow mb-1.5 flex items-center gap-1.5">
      <span>{label}</span>
      <span
        title={help || label}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[hsl(var(--border))] text-[10px] text-[hsl(var(--muted-foreground))] normal-case tracking-normal"
      >
        ?
      </span>
    </div>
  );
}

function Input({ label = "", value, onChange, required, test, type = "text", help = "" } = {}) {
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={test} className="w-full h-9 bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm" />
    </div>
  );
}

function TextAreaInput({ label = "", value, onChange, rows = 3, test, help = "" } = {}) {
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={test}
        className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm"
      />
    </div>
  );
}

function Number({ label = "", value, onChange, test, help = "" } = {}) {
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <input type="number" min={0} value={value} onChange={(e) => onChange(parseInt(e.target.value || 0, 10))} data-testid={test} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm font-mono" />
    </div>
  );
}

function Range({ label = "", value, onChange, test, help = "" } = {}) {
  const parsed = globalThis.Number(value);
  const display = globalThis.Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
  return (
    <div>
      <LabelWithTip label={`${label}: ${display}`} help={help} />
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(event) => onChange(parseFloat(event.target.value))}
        data-testid={test}
        className="w-full"
      />
    </div>
  );
}

function Select({ label = "", value, onChange, options, test, help = "" } = {}) {
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={test} className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function ComboSelect({ label = "", value, onChange, options, placeholder, test, disabled, onFocus, help = "" } = {}) {
  const normalizedOptions = [...(options || [])];
  if (value && !normalizedOptions.some(([v]) => v === value)) {
    normalizedOptions.unshift([value, value]);
  }
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        disabled={disabled}
        data-testid={test}
        className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm disabled:opacity-50"
      >
        <option value="">{placeholder || "Select"}</option>
        {normalizedOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function ObsLookupField({ options, ...props }) {
  if ((options || []).length > 0) {
    return <ComboSelect {...props} options={options} />;
  }
  return <DatalistInput {...props} options={options} />;
}

function DatalistInput({ label = "", value, onChange, options, placeholder, test, disabled, onFocus, onBlur, help = "" } = {}) {
  const listId = `${test || label}-options`;
  return (
    <div>
      <LabelWithTip label={label} help={help} />
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        list={listId}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={test}
        className="w-full bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] rounded px-3 py-2 text-sm disabled:opacity-50"
      />
      <datalist id={listId}>
        {(options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </datalist>
    </div>
  );
}

async function flattenObsSceneSources(sceneItems, obsRequest, depth = 0) {
  const names = [];
  const seen = new Set();
  const addName = (name) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    names.push(name);
  };

  for (const item of sceneItems || []) {
    const sourceName = item.sourceName;
    if (!sourceName) continue;

    try {
      const groupData = await obsRequest("GetGroupSceneItemList", { sceneName: sourceName });
      const groupItems = groupData.sceneItems || [];
      if (groupItems.length > 0 && depth < 4) {
        const childNames = await flattenObsSceneSources(groupItems, obsRequest, depth + 1);
        childNames.forEach(addName);
        continue;
      }
    } catch (err) {
      // Normal sources are not groups; keep them as selectable OBS sources.
    }

    addName(sourceName);
  }

  return names;
}

function Toggle({ label = "", checked, onChange, test, help = "" } = {}) {
  return (
    <label className="text-xs flex items-center gap-2 cursor-pointer pb-2" data-testid={test}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
      <span title={help || label} className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-[hsl(var(--border))] text-[10px] text-[hsl(var(--muted-foreground))]">?</span>
    </label>
  );
}
